import {DataTable, dateTime} from '@management-ui/core';
import {MTableToolbar} from '@material-table/core';
import {reverse} from 'named-urls';
import * as React from 'react';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import ContactTypes from '../../../../components/ContactTypes';
import Materials from '../../../../components/Materials';
import {ServiceContext} from '../../../../components/Services';
import {useQuery} from '../../../../hooks';
import routes from '../../../../routes';
import {API} from '../../../../services/API';
import Filter from './Filter';

const Table = ({archive = false, byMaterial = null}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materials, setMaterials] = useState([]);

  /** @type {({current: DataTable})} */
  const tableRef = useRef();
  const refreshRef = useRef('');
  const {
    values: {type, material, page, pageSize, search},
    changeQuery,
    changePage,
    initialised
  } = useQuery(useMemo(() => ({
    type: 'all',
    material: byMaterial ?? '',
    page: '1',
    pageSize: '50',
    search: ''
  }), [byMaterial]));

  useEffect(() => {
    const key = JSON.stringify({type, material});
    if (initialised && refreshRef.current !== key && tableRef.current) {
      tableRef.current.refresh();
    }
    refreshRef.current = key;
  }, [type, material, initialised]);

  useEffect(() => {
    if (byMaterial) {
      setMaterials([]);
    } else {
      services.material.getMaterials({}, 1, 500)
        .then((response) => setMaterials(response.data))
        .catch(() => setMaterials([]));
    }
  }, [byMaterial, services]);

  useEffect(() => {
    setSelectedMaterial(materials.find(m => `${m.id}` === material));
  }, [materials, material]);

  const tableProps = useMemo(() => ({
    tableProps: {
      components: {
        Toolbar: (props) => (
          <>
            <MTableToolbar {...props}/>
            <Filter
              type={type}
              onChangeType={(changed) => changeQuery({type: changed})}
              materials={materials}
              material={selectedMaterial}
              onChangeMaterial={(changed) => changeQuery({material: changed ? `${changed.id}` : ''})}
            />
          </>
        )
      }
    },
    options: {
      initialPage: parseInt(page) - 1,
      pageSize: parseInt(pageSize),
      searchText: search
    },
    columns: [
      {title: 'Name', field: 'name'},
      {title: 'Customer Code', field: 'customer_code'},
      {title: 'Supplier Code', field: 'supplier_code'},
      {title: 'Types', sorting: false, field: 'types', render: item => <ContactTypes contact={item}/>},
      {
        title: 'Materials',
        sorting: false,
        field: 'materials',
        render: item => <Materials materials={item.materials}/>
      },
      {title: 'Last Updated', field: 'updated_at', render: item => dateTime(item.updated_at)},
    ],
    loadData: query => new Promise((resolve, reject) => {
      const params = {};
      if (type === 'customers') {
        params.customers = '1';
      } else if (type === 'suppliers') {
        params.suppliers = '1';
      } else if (type === 'prospects') {
        params.prospects = '1';
      } else if (type === 'transporters') {
        params.transporters = '1';
      }
      if (archive) {
        params.archive = 'only';
      }
      if (material) {
        params.materials = material;
      }
      changePage(API.getTableParams(query));
      services.contact.getTableContacts(query, params)
        .then(response => {
          resolve({
            data: response.data, page: response.meta.current_page - 1, totalCount: response.meta.total
          });
        }).catch(() => reject());
    }),
    onRowClick: (contact) => history.push(reverse(contact.archived ? routes.archive.contact.detail : routes.contacts.detail, {id: contact.id}))
  }), [archive, services, history, materials, selectedMaterial, type, material, page, pageSize, search, changeQuery, changePage]);

  return initialised ? (
    <DataTable
      ref={tableRef}
      title=""
      {...tableProps}
    />
  ) : null;
};

export default Table;
